import { useId } from "react";
import type { SelectHTMLAttributes } from "react";

import { cx } from "../lib/cx";
import { FieldShell, controlClass } from "./FieldShell";

export interface ClaySelectOption {
  value: string;
  label: string;
}

export interface ClaySelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "className"> {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: Array<ClaySelectOption | string>;
  placeholder?: string;
  error?: string | null;
  hint?: string;
  className?: string;
  hideLabel?: boolean;
}

export function ClaySelect({
  label,
  value,
  onValueChange,
  options,
  placeholder,
  error,
  hint,
  className,
  hideLabel = false,
  id,
  required,
  ...rest
}: ClaySelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const normalized: ClaySelectOption[] = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );

  const control = (
    <div className="relative">
      <select
        {...rest}
        id={selectId}
        value={value}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${selectId}-error` : undefined}
        onChange={(event) => onValueChange(event.target.value)}
        className={cx(
          controlClass,
          "appearance-none pr-10",
          error && "shadow-[0_0_0_2px_rgba(244,63,94,0.45)]",
        )}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {normalized.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      >
        <path d="M6 8l4 4 4-4" />
      </svg>
    </div>
  );

  if (hideLabel) {
    return (
      <div className="flex flex-col gap-2">
        <label htmlFor={selectId} className="sr-only">
          {label}
        </label>
        {control}
      </div>
    );
  }

  return (
    <FieldShell
      id={selectId}
      label={label}
      required={required}
      error={error}
      hint={hint}
      className={className}
    >
      {control}
    </FieldShell>
  );
}
